import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Calendar, Mail, Phone, ArrowRight, Loader2, XCircle } from "lucide-react";
import axios from "axios";
import { trackPurchase } from "../utils/metaTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("loading"); // loading, success, failed, expired
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/bookings/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          setBooking(response.data.booking);
          // Meta: Purchase on confirmed experience booking
          const b = response.data.booking;
          if (b) {
            trackPurchase({
              orderId: b.id || sessionId,
              bookingCode: b.booking_code || '',
              name: b.name || '',
              email: b.email || '',
              phone: b.phone || '',
              title: b.package_name || 'Experience',
              slug: '',
              value: b.total_amount || 0,
              currency: 'CAD',
              quantity: b.guest_count || 1,
              businessLine: 'experiences',
              ticketType: b.package_name || '',
            });
          }
        } else if (response.data.status === "expired") {
          setStatus("expired");
        } else if (attempts < maxAttempts - 1) {
          // Still pending, try again
          setAttempts(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          // Max attempts reached
          setStatus("failed");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts < maxAttempts - 1) {
          setAttempts(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          setStatus("failed");
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <main className="pt-20 min-h-screen flex items-center" data-testid="booking-success-page">
      <div className="container-luxury py-20">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center"
        >
          {/* Loading State */}
          {status === "loading" && (
            <div data-testid="loading-state">
              <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
                Processing Payment...
              </h1>
              <p className="text-[#A1A1AA] mb-8">
                Please wait while we confirm your payment. This may take a few moments.
              </p>
              <div className="flex justify-center gap-2">
                {[...Array(maxAttempts)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i <= attempts ? "bg-[#D4AF37]" : "bg-[rgba(212,175,55,0.3)]"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Success State */}
          {status === "success" && (
            <div data-testid="success-state">
              <div className="success-checkmark mb-8">
                <CheckCircle className="w-10 h-10" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
                Booking Confirmed!
              </h1>
              <p className="text-[#A1A1AA] text-lg mb-12">
                Thank you for choosing Élixir Events. Your unforgettable celebration awaits.
              </p>

              {booking && (
                <div className="luxury-card p-8 text-left mb-12">
                  <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6">
                    Booking Details
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 pb-4 border-b border-[rgba(212,175,55,0.15)]">
                      <Calendar className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                      <div>
                        <p className="text-[#52525B] text-sm">Event Date</p>
                        <p className="text-[#F5F5F0]">{booking.event_date}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 pb-4 border-b border-[rgba(212,175,55,0.15)]">
                      <div className="w-5 h-5 flex items-center justify-center text-[#D4AF37] text-sm font-bold">
                        ✦
                      </div>
                      <div>
                        <p className="text-[#52525B] text-sm">Package</p>
                        <p className="text-[#F5F5F0]">{booking.package_name}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 pb-4 border-b border-[rgba(212,175,55,0.15)]">
                      <Mail className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                      <div>
                        <p className="text-[#52525B] text-sm">Confirmation Email</p>
                        <p className="text-[#F5F5F0]">{booking.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <Phone className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                      <div>
                        <p className="text-[#52525B] text-sm">Contact Phone</p>
                        <p className="text-[#F5F5F0]">{booking.phone}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[rgba(212,175,55,0.15)]">
                    {booking.is_deposit ? (
                      <>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[#A1A1AA]">Package Price</span>
                          <span className="text-[#F5F5F0]">
                            C${((booking.deposit_amount || booking.total_amount) / 0.6).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[#D4AF37] font-semibold">60% Deposit Paid Today</span>
                          <span className="text-xl font-['Playfair_Display'] font-bold gold-text">
                            C${(booking.deposit_amount || booking.total_amount)?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-[rgba(212,175,55,0.1)]">
                          <span className="text-[#A1A1AA]">Balance Due Later</span>
                          <span className="text-[#F5F5F0]">
                            C${(booking.balance_due || 0)?.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-[#52525B] text-xs mt-4">
                          We'll contact you to discuss your requirements and finalize the balance.
                        </p>
                      </>
                    ) : (
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Total Paid</span>
                        <span className="text-2xl font-['Playfair_Display'] font-bold gold-text">
                          C${booking.total_amount?.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/" 
                  className="btn-secondary"
                  data-testid="back-home-btn"
                >
                  Back to Home
                </Link>
                <Link 
                  to="/gallery" 
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="view-gallery-btn"
                >
                  View Our Gallery
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {/* Failed State */}
          {status === "failed" && (
            <div data-testid="failed-state">
              <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center border-2 border-red-500 rounded-full">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
                Payment Issue
              </h1>
              <p className="text-[#A1A1AA] text-lg mb-8">
                We couldn't confirm your payment. Please try again or contact us for assistance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/booking" 
                  className="btn-primary"
                  data-testid="try-again-btn"
                >
                  Try Again
                </Link>
                <Link 
                  to="/contact" 
                  className="btn-secondary"
                  data-testid="contact-support-btn"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          )}

          {/* Expired State */}
          {status === "expired" && (
            <div data-testid="expired-state">
              <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center border-2 border-[#D4AF37] rounded-full">
                <Calendar className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
                Session Expired
              </h1>
              <p className="text-[#A1A1AA] text-lg mb-8">
                Your payment session has expired. Please start a new booking.
              </p>
              <Link 
                to="/booking" 
                className="btn-primary"
                data-testid="new-booking-btn"
              >
                Start New Booking
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
};

export default BookingSuccessPage;
